import React, { useState, useEffect } from 'react';
import UgcTermsCheckbox from './UgcTermsCheckbox';

const API      = 'https://v-room.app/api/auth';
const SAPI     = 'https://v-room.app/sapi/auth';

export default function AuthModal({ onClose, onLogin, defaultReferralCode = '', onShowTerms, onShowPrivacy }) {
  // tab: 'login' | 'register' | 'forgot' | 'verify' | 'reset'
  const [tab,          setTab]          = useState('login');
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [username,     setUsername]     = useState('');
  const [confirmPass,  setConfirmPass]  = useState('');
  const [referralCode, setReferralCode] = useState(defaultReferralCode);
  const [showPass,     setShowPass]     = useState(false);
  const [showNewPass,  setShowNewPass]  = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [successMsg,   setSuccessMsg]   = useState('');
  const [refValid,     setRefValid]     = useState(null);
  const [acceptedUgcTerms, setAcceptedUgcTerms] = useState(false);

  // reset hasła
  const [resetCode,    setResetCode]    = useState('');
  const [newPassword,  setNewPassword]  = useState('');
  const [confirmNew,   setConfirmNew]   = useState('');

  const switchTab = (t) => {
    setTab(t); setError(''); setSuccessMsg('');
    setResetCode(''); setNewPassword(''); setConfirmNew('');
    if (t === 'forgot' || t === 'verify' || t === 'reset') setAcceptedUgcTerms(false);
  };

  const requireUgcTerms = () => {
    if (!acceptedUgcTerms) {
      setError('Zaznacz zgodę na Regulamin i Politykę prywatności.');
      return false;
    }
    return true;
  };

  // ── Weryfikuj kod referralowy ──────────────────────────────────────────────
  useEffect(() => {
    if (referralCode.length < 4) { setRefValid(null); return; }
    const timeout = setTimeout(async () => {
      try {
        const res  = await fetch(`https://v-room.app/api/referral/check/${referralCode.toUpperCase().trim()}`);
        const data = await res.json();
        setRefValid(res.ok && data.valid ? data : false);
      } catch { setRefValid(null); }
    }, 600);
    return () => clearTimeout(timeout);
  }, [referralCode]);

  // ── Logowanie ────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!requireUgcTerms()) return;
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/login`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Vroom-Web': '1' },
        body: JSON.stringify({ email: email.trim(), password, acceptUgcTerms: true }),
      });
      const data = await res.json();
      if (res.ok) { onLogin({ ...data.user, token: data.token, csrfToken: data.csrfToken }); }
      else         { setError(data.error || 'Nieprawidłowe dane logowania'); }
    } catch { setError('Błąd połączenia z serwerem'); }
    finally  { setLoading(false); }
  };

  // ── Rejestracja ───────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!requireUgcTerms()) return;
    if (password !== confirmPass) { setError('Hasła nie są identyczne'); return; }
    if (password.length < 6)      { setError('Hasło musi mieć min. 6 znaków'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(), password,
          username: username.trim(),
          referralCode: referralCode.trim() || undefined,
          acceptUgcTerms: true,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg('✅ Konto utworzone! Możesz się teraz zalogować.');
        setPassword(''); setConfirmPass(''); setUsername(''); setReferralCode('');
        setTimeout(() => switchTab('login'), 1500);
      } else { setError(data.error || 'Nie można utworzyć konta'); }
    } catch { setError('Błąd połączenia z serwerem'); }
    finally  { setLoading(false); }
  };

  // ── Krok 1: Wyślij kod na email ───────────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Podaj adres e-mail'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${SAPI}/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg('📧 Kod wysłany! Sprawdź skrzynkę e-mail.');
        setTimeout(() => { setSuccessMsg(''); switchTab('verify'); }, 1500);
      } else { setError(data.message || 'Błąd wysyłania'); }
    } catch { setError('Błąd połączenia z serwerem'); }
    finally  { setLoading(false); }
  };

  // ── Krok 2: Weryfikuj 6-cyfrowy kod ─────────────────────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (resetCode.length !== 6) { setError('Kod musi mieć 6 cyfr'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${SAPI}/verify-reset-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: resetCode }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg('✅ Kod poprawny!');
        setTimeout(() => {
          setSuccessMsg('');
          setError('');
          setTab('reset'); // ← setTab bezpośrednio, nie switchTab
        }, 1000);
      } else { setError(data.message || 'Nieprawidłowy kod'); }
    } catch { setError('Błąd połączenia z serwerem'); }
    finally  { setLoading(false); }
  };

  // ── Krok 3: Ustaw nowe hasło — bez tokenu, tylko email + kod ─────────────
  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6)    { setError('Hasło musi mieć min. 6 znaków'); return; }
    if (newPassword !== confirmNew) { setError('Hasła nie są identyczne'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${SAPI}/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:       email.trim(),   // ← email zamiast tokenu
          code:        resetCode,      // ← kod zamiast tokenu
          newPassword,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg('✅ Hasło zmienione! Możesz się zalogować.');
        setTimeout(() => switchTab('login'), 2000);
      } else { setError(data.message || 'Błąd resetu hasła'); }
    } catch { setError('Błąd połączenia z serwerem'); }
    finally  { setLoading(false); }
  };

  // ── Helpers UI ────────────────────────────────────────────────────────────
  const isForgotFlow = ['forgot', 'verify', 'reset'].includes(tab);

  const stepIndicator = isForgotFlow && (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
      {[
        { key: 'forgot', label: '1. Email' },
        { key: 'verify', label: '2. Kod'   },
        { key: 'reset',  label: '3. Hasło' },
      ].map((s, i) => {
        const steps   = ['forgot', 'verify', 'reset'];
        const current = steps.indexOf(tab);
        const done    = steps.indexOf(s.key) < current;
        const active  = s.key === tab;
        return (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 12,
              backgroundColor: done ? '#4de926' : active ? '#e33835' : '#ffffff15',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff',
            }}>
              {done ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 10, color: active ? '#e33835' : done ? '#4de926' : '#ffffff40', fontFamily: 'inherit' }}>
              {s.label}
            </span>
            {i < 2 && <span style={{ color: '#ffffff20', fontSize: 12 }}>›</span>}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>

        <div className="auth-logo">
          <span className="material-icons">speed</span>
          VROOM
        </div>

        {/* TABS — ukryj podczas forgot flow */}
        {!isForgotFlow && (
          <div className="auth-tabs">
            <button className={tab === 'login'    ? 'active' : ''} onClick={() => switchTab('login')}>LOGOWANIE</button>
            <button className={tab === 'register' ? 'active' : ''} onClick={() => switchTab('register')}>REJESTRACJA</button>
          </div>
        )}

        {/* Nagłówek forgot flow */}
        {isForgotFlow && (
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <button
              onClick={() => switchTab('login')}
              style={{ background: 'none', border: 'none', color: '#e33835', cursor: 'pointer', fontSize: 12, marginBottom: 10 }}
            >
              ← Wróć do logowania
            </button>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', letterSpacing: 1 }}>RESET HASŁA</div>
          </div>
        )}

        {stepIndicator}

        {/* ── LOGOWANIE ── */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-field">
              <span className="material-icons">email</span>
              <input type="email" placeholder="Adres e-mail" value={email}
                onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="auth-field">
              <span className="material-icons">lock</span>
              <input type={showPass ? 'text' : 'password'} placeholder="Hasło"
                value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="button" className="auth-eye" onClick={() => setShowPass(v => !v)}>
                <span className="material-icons">{showPass ? 'visibility' : 'visibility_off'}</span>
              </button>
            </div>

            <UgcTermsCheckbox
              checked={acceptedUgcTerms}
              onChange={setAcceptedUgcTerms}
              onShowTerms={onShowTerms}
              onShowPrivacy={onShowPrivacy}
              hint="Wymagane przed logowaniem."
            />

            {error      && <div className="auth-error">{error}</div>}
            {successMsg && <div className="auth-success">{successMsg}</div>}

            <button type="submit" className="auth-submit" disabled={loading || !acceptedUgcTerms}>
              {loading ? 'Ładowanie...' : 'ZALOGUJ SIĘ'}
            </button>

            <button type="button" className="auth-forgot-link" onClick={() => switchTab('forgot')}>
              Zapomniałeś hasła?
            </button>
          </form>
        )}

        {/* ── REJESTRACJA ── */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="auth-form">
            <div className="auth-field">
              <span className="material-icons">person</span>
              <input type="text" placeholder="Nazwa użytkownika" value={username}
                onChange={e => setUsername(e.target.value)} required autoComplete="off" />
            </div>
            <div className="auth-field">
              <span className="material-icons">email</span>
              <input type="email" placeholder="Adres e-mail" value={email}
                onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="auth-field">
              <span className="material-icons">lock</span>
              <input type={showPass ? 'text' : 'password'} placeholder="Hasło"
                value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="button" className="auth-eye" onClick={() => setShowPass(v => !v)}>
                <span className="material-icons">{showPass ? 'visibility' : 'visibility_off'}</span>
              </button>
            </div>
            <div className="auth-field">
              <span className="material-icons">check_circle</span>
              <input type="password" placeholder="Potwierdź hasło"
                value={confirmPass} onChange={e => setConfirmPass(e.target.value)} required />
            </div>

            {/* KOD REFERRALOWY */}
            <div className="auth-field" style={{
              borderColor: refValid === false ? '#e33835' : refValid ? '#4de926' : undefined,
            }}>
              <span className="material-icons" style={{
                color: refValid === false ? '#e33835' : refValid ? '#4de926' : undefined,
              }}>card_giftcard</span>
              <input type="text" placeholder="Kod zaproszenia (opcjonalnie)"
                value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())}
                maxLength={16} autoComplete="off"
                style={{ textTransform: 'uppercase', letterSpacing: 2 }} />
              {refValid && <span className="material-icons" style={{ color: '#4de926', fontSize: 18 }}>check_circle</span>}
              {refValid === false && <span className="material-icons" style={{ color: '#e33835', fontSize: 18 }}>cancel</span>}
            </div>
            {refValid && (
              <div className="auth-ref-info">
                🎉 Zaproszony przez <strong>{refValid.invitedBy}</strong> — dostaniesz <strong>+10 pkt</strong> na start!
              </div>
            )}
            {refValid === false && referralCode.length >= 4 && (
              <div className="auth-ref-error">Nieprawidłowy kod zaproszenia</div>
            )}

            <UgcTermsCheckbox
              checked={acceptedUgcTerms}
              onChange={setAcceptedUgcTerms}
              onShowTerms={onShowTerms}
              onShowPrivacy={onShowPrivacy}
              hint="Wymagane przed rejestracją."
            />

            {error      && <div className="auth-error">{error}</div>}
            {successMsg && <div className="auth-success">{successMsg}</div>}

            <button type="submit" className="auth-submit" disabled={loading || !acceptedUgcTerms}>
              {loading ? 'Ładowanie...' : 'UTWÓRZ KONTO'}
            </button>
          </form>
        )}

        {/* ── KROK 1: PODAJ EMAIL ── */}
        {tab === 'forgot' && (
          <form onSubmit={handleForgot} className="auth-form">
            <p style={{ color: '#ffffff70', fontSize: 13, textAlign: 'center', margin: '0 0 16px' }}>
              Podaj adres e-mail powiązany z kontem. Wyślemy 6-cyfrowy kod.
            </p>
            <div className="auth-field">
              <span className="material-icons">email</span>
              <input type="email" placeholder="Adres e-mail" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus />
            </div>
            {error      && <div className="auth-error">{error}</div>}
            {successMsg && <div className="auth-success">{successMsg}</div>}
            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Wysyłanie...' : 'WYŚLIJ KOD'}
            </button>
          </form>
        )}

        {/* ── KROK 2: WPISZ KOD ── */}
        {tab === 'verify' && (
          <form onSubmit={handleVerify} className="auth-form">
            <p style={{ color: '#ffffff70', fontSize: 13, textAlign: 'center', margin: '0 0 16px' }}>
              Wpisz 6-cyfrowy kod wysłany na <strong style={{ color: '#fff' }}>{email}</strong>
            </p>
            <div className="auth-field" style={{ justifyContent: 'center' }}>
              <span className="material-icons">pin</span>
              <input
                type="text" placeholder="000000" value={resetCode}
                onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required maxLength={6} autoFocus
                style={{ textAlign: 'center', fontSize: 28, letterSpacing: 10, fontWeight: 700 }}
              />
            </div>
            {error      && <div className="auth-error">{error}</div>}
            {successMsg && <div className="auth-success">{successMsg}</div>}
            <button type="submit" className="auth-submit" disabled={loading || resetCode.length !== 6}>
              {loading ? 'Weryfikacja...' : 'POTWIERDŹ KOD'}
            </button>
            <button type="button" className="auth-forgot-link" onClick={() => switchTab('forgot')}>
              Wyślij kod ponownie
            </button>
          </form>
        )}

        {/* ── KROK 3: NOWE HASŁO ── */}
        {tab === 'reset' && (
          <form onSubmit={handleReset} className="auth-form">
            <p style={{ color: '#ffffff70', fontSize: 13, textAlign: 'center', margin: '0 0 16px' }}>
              Ustaw nowe hasło do swojego konta.
            </p>
            <div className="auth-field">
              <span className="material-icons">lock_reset</span>
              <input type={showNewPass ? 'text' : 'password'} placeholder="Nowe hasło (min. 6 znaków)"
                value={newPassword} onChange={e => setNewPassword(e.target.value)} required autoFocus />
              <button type="button" className="auth-eye" onClick={() => setShowNewPass(v => !v)}>
                <span className="material-icons">{showNewPass ? 'visibility' : 'visibility_off'}</span>
              </button>
            </div>
            <div className="auth-field" style={{
              borderColor: confirmNew && newPassword !== confirmNew ? '#e33835'
                         : confirmNew && newPassword === confirmNew ? '#4de926' : undefined,
            }}>
              <span className="material-icons">check_circle</span>
              <input type="password" placeholder="Potwierdź nowe hasło"
                value={confirmNew} onChange={e => setConfirmNew(e.target.value)} required />
              {confirmNew && newPassword === confirmNew && (
                <span className="material-icons" style={{ color: '#4de926', fontSize: 18 }}>check_circle</span>
              )}
            </div>
            {error      && <div className="auth-error">{error}</div>}
            {successMsg && <div className="auth-success">{successMsg}</div>}
            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Zapisywanie...' : 'ZAPISZ NOWE HASŁO'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
